import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, DollarSign, BarChart2 } from "lucide-react";

function OpenAILogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.911 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.182a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .511 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.041l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95A4.5 4.5 0 0 1 3.6 18.304zm-1.26-10.43A4.485 4.485 0 0 1 4.704 5.9l.001 5.7a.766.766 0 0 0 .388.677l5.814 3.354-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.787A4.504 4.504 0 0 1 2.34 7.874zm16.597 3.855-5.785-3.339 2.015-1.163a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.104v-5.677a.79.79 0 0 0-.407-.667l-.048-.049zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.41 9.228V6.896a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.037a4.5 4.5 0 0 1 7.376-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
    </svg>
  );
}
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import type { CMA, CMAComp } from "@/hooks/useCMAData";

interface CMAPricingDisplayProps {
  cma: CMA;
  comps: CMAComp[];
}

function formatPrice(n: number) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

export function CMAPricingDisplay({ cma, comps }: CMAPricingDisplayProps) {
  const visibleComps = comps.filter((c) => c.visible);

  const stats = useMemo(() => {
    if (visibleComps.length === 0) return null;

    const prices = visibleComps.map((c) => c.price || 0).filter((p) => p > 0);
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    const ppsqValues = visibleComps.map((c) => c.price_per_sqft || 0).filter((p) => p > 0);
    const avgPpsq = ppsqValues.length > 0 ? ppsqValues.reduce((a, b) => a + b, 0) / ppsqValues.length : 0;

    const domValues = visibleComps.map((c) => c.days_on_market || 0).filter((d) => d > 0);
    const avgDom = domValues.length > 0 ? domValues.reduce((a, b) => a + b, 0) / domValues.length : 0;

    return { avgPrice, minPrice, maxPrice, avgPpsq, avgDom };
  }, [visibleComps]);

  const chartData = useMemo(() => {
    const data = visibleComps
      .filter((c) => c.price && c.price > 0)
      .slice(0, 8)
      .map((c) => ({
        name: c.address ? c.address.split(" ").slice(0, 2).join(" ") : "?",
        price: c.price || 0,
        status: c.status || "UNKNOWN",
      }));

    if (cma.suggested_price_low && cma.suggested_price_high) {
      data.push({
        name: "Subject",
        price: Math.round((cma.suggested_price_low + cma.suggested_price_high) / 2),
        status: "SUBJECT",
      });
    }
    return data;
  }, [visibleComps, cma]);

  const getBarColor = (status: string) => {
    if (status === "SUBJECT") return cma.primary_brand_color || "#0c0f24";
    if (status === "SOLD" || status?.includes("SOLD")) return "#6b7280";
    return "#10b981";
  };

  if (!cma.suggested_price_low && !cma.suggested_price_high && visibleComps.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Run the AI analysis to generate a pricing recommendation.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Price Range Card */}
      {(cma.suggested_price_low || cma.suggested_price_high) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              AI Suggested List Price
              <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground border border-border rounded-full px-2 py-0.5 ml-1">
                <OpenAILogo className="h-3 w-3" />
                OpenAI
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-4xl font-bold tracking-tight" style={{ color: cma.primary_brand_color || "#0c0f24" }}>
                {cma.suggested_price_low && cma.suggested_price_high
                  ? `${formatPrice(cma.suggested_price_low)} – ${formatPrice(cma.suggested_price_high)}`
                  : cma.suggested_price_low
                  ? `From ${formatPrice(cma.suggested_price_low)}`
                  : `Up to ${formatPrice(cma.suggested_price_high!)}`}
              </p>
              {stats && (
                <p className="text-sm text-muted-foreground mt-1">
                  Based on {visibleComps.length} comparable {visibleComps.length === 1 ? "property" : "properties"}
                </p>
              )}
            </div>

            {stats && (
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Avg Comp Price</p>
                  <p className="font-semibold text-sm">{formatPrice(Math.round(stats.avgPrice))}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Avg $/SqFt</p>
                  <p className="font-semibold text-sm">{stats.avgPpsq > 0 ? `$${Math.round(stats.avgPpsq)}` : "—"}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Median DOM</p>
                  <p className="font-semibold text-sm">
                    {(() => {
                      const zipDom = cma.zip_sold_dom_median ?? cma.property_data?.zip_sold_dom_median ?? null;
                      const dom = zipDom ?? (stats.avgDom > 0 ? Math.round(stats.avgDom) : null);
                      return dom != null ? `${dom} days` : "—";
                    })()}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comp Price Chart */}
      {chartData.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart2 className="h-4 w-4" />
              Price Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: cma.primary_brand_color || "#0c0f24" }} />
                <span>Subject</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-emerald-500" />
                <span>Active</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-gray-500" />
                <span>Sold</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                  width={50}
                />
                <Tooltip
                  formatter={(value: number) => [`$${value.toLocaleString()}`, "Price"]}
                  labelStyle={{ fontSize: 11 }}
                  contentStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={getBarColor(entry.status)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
